/* oxlint-disable max-lines */
import { setup } from 'xstate';

import type { StateActions, StateContext, StateEvents, StateGuards, StateInput } from '../../types';

export const machine = setup({
	/* oxlint-disable typescript/no-unsafe-type-assertion, typescript/no-unnecessary-type-assertion -- xstate setup() infers context/input/events from the {} literal; the cast is how these get typed at all */
	types: { context: {} as StateContext, events: {} as StateEvents, input: {} as StateInput },
	actions: {} as StateActions,
	guards: {} as StateGuards,
	/* oxlint-enable typescript/no-unsafe-type-assertion, typescript/no-unnecessary-type-assertion */
}).createMachine({
	id: 'start-a-petition-chat-agent',
	initial: 'intro',
	context: ({ input }) => input,
	states: {
		intro: {
			on: {
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: 'if user opts out of chat experience',
						target: 'start',
						guard: 'didOptOut',
						actions: ['onSubmit'],
					},
					{
						description: 'user completed chat flow and created a draft',
						target: 'preview',
						guard: 'isAiDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
				],
				error: [
					{
						description: 'chat shell failed to load, but we have the seeded issue — continue the AI flow manually',
						target: 'personal-story',
						guard: 'isLoadTimeoutWithIssue',
						actions: ['onLoadTimeout'],
					},
					{
						description: 'chat shell failed to load with nothing to go on — back to the flow chooser',
						target: 'start',
						guard: 'isLoadTimeout',
						actions: ['onLoadTimeout'],
					},
					{
						description: 'draft generation failed — fall back to the manual create flow',
						target: 'create',
						actions: ['onError'],
					},
				],
			},
		},

		start: {
			on: {
				submit: [
					{
						description: 'if user chose the ai driven flow',
						target: 'issue',
						guard: 'isAiDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
					{
						description: 'if user chose the user driven flow',
						target: 'create',
						guard: 'isUserDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
				],
			},
		},

		// START: `ai_draft` only steps - includes chat agent
		issue: {
			on: {
				back: [{ target: 'start', actions: ['unsetDraftType'] }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'personal-story', actions: ['onSubmit'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		'personal-story': {
			on: {
				back: [{ target: 'issue' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		preview: {
			on: {
				back: [{ target: 'intro', guard: 'isChatFlow' }, { target: 'personal-story' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					// -- editing transitions should come before step submissions
					{
						description: 'transition to edit step',
						target: 'edit',
						guard: { type: 'transitionTo', params: { target: 'edit' } },
						actions: ['onSubmit'],
					},
					{
						description: 'transition to next step',
						target: 'location',
						actions: ['onSubmit'],
					},
				],
			},
		},
		edit: {
			on: {
				back: [
					// -- edit step only fires `submit` events
				],
				submit: [
					// -- edit transitions do not need conditional transitions based on draft.type
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'preview',
						guard: { type: 'transitionTo', params: { target: 'preview' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
					},
					{
						description: 'default submit if no target specified',
						target: 'location',
					},
				],
			},
		},
		// END: `ai_draft` only steps

		// START: `user_draft` only steps
		create: {
			on: {
				back: [
					{
						target: 'start',
						guard: 'isUserDraft',
						actions: ['unsetDraftType'],
					},
				],
				submit: [
					// -- create transitions do not need conditional transitions based on draft.type
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'optional-preview',
						guard: { type: 'transitionTo', params: { target: 'optional-preview' } },
						actions: ['onSubmit'],
					},
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
						actions: ['onSubmit'],
					},
					{ target: 'location', actions: ['onSubmit'] },
				],
			},
		},
		'optional-preview': {
			on: {
				back: [
					{
						description: 'optional-preview only goes back to create',
						target: 'create',
					},
				],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: 'TODO: why?',
						target: 'create',
						guard: { type: 'transitionTo', params: { target: 'create' } },
					},
					{ target: 'location' },
				],
			},
		},
		// END: `user_draft` only steps

		// START: common steps
		media: {
			on: {
				back: [
					{
						description: "{ draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
					},
					{
						description: "{ draft: { type: 'ai_draft' } }",
						target: 'edit',
						guard: 'isAiDraft',
					},
				],
				submit: [
					{
						description: "{ draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ draft: { type: 'ai_draft' } }",
						target: 'edit',
						guard: 'isAiDraft',
						actions: ['onSubmit'],
					},
				],
			},
		},
		location: {
			on: {
				back: [
					{
						description: "{ draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
					},
					{
						description: "{ draft: { type: 'ai_draft' } }",
						target: 'preview',
						guard: 'isAiDraft',
					},
				],
				submit: [
					// -- location submit transitions do not need conditional transitions based on draft.type
					{
						target: 'prepublish',
						actions: ['onSubmit'],
					},
				],
			},
		},
		prepublish: {
			on: {
				back: { target: 'location' },
			},
		},
		// END: common steps

		// TODO: move to their own machine
		'chat-app-entry': {
			on: {
				back: [{ target: 'intro' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit', 'persistDraftType'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		social: {
			on: {
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit', 'persistDraftType'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
	},
});
